import React, { useState, forwardRef, Ref } from "react"
import { useTheme } from "@mui/material/styles"
import DatePicker, { registerLocale } from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import { enUS } from "date-fns/locale/en-US"
import { ru } from "date-fns/locale/ru"
import TextField from "@mui/material/TextField"
import Box from "@mui/material/Box"
import { useTranslate, useLocale } from "ra-core"

//TODO: объединить с i18n компонентом
registerLocale("en", enUS)
registerLocale("ru", ru)

interface CustomInputProps {
  onClick?: () => void
  value?: string
  width?: string | number
}

interface DateRangePickerProps {
  width?: string | number
  onRangeChange?: (dateRange: [Date | null, Date | null]) => void
}

function DateRangePicker({ width = "100%", onRangeChange }: DateRangePickerProps = {}): JSX.Element {
  const theme = useTheme()
  const translate = useTranslate()
  const locale = useLocale()
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null])
  const [startDate, endDate] = dateRange

  const darkStyles = {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    '& .react-datepicker__header': {
      backgroundColor: theme.palette.background.paper,
    },
    '& .react-datepicker__day': {
      color: theme.palette.text.primary,
    },
    '& .react-datepicker__day--selected': {
      backgroundColor: theme.palette.primary.main,
    },
    '& .react-datepicker__current-month': {
      color: theme.palette.text.primary,
    },
    '& .react-datepicker__day-name': {
      color: theme.palette.text.secondary,
    }
  }

  const getDateRangeText = (): string => {
    if (startDate && endDate) {
      const formatDate = (date: Date): string => {
        return date.toLocaleDateString(locale, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric"
        })
      }
      return `${formatDate(startDate)} - ${formatDate(endDate)}`
    } else if (startDate) {
      return startDate.toLocaleDateString(locale)
    }
    return ""
  }

  const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>(
    ({ onClick, width: inputWidth }, ref) => (
      <TextField
        variant="outlined"
        onClick={onClick}
        ref={ref as Ref<HTMLDivElement>}
        value={getDateRangeText()}
        label={translate("internal.daterange")}
        InputProps={{
          readOnly: true,
          sx: {
            color: theme.palette.text.primary,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.divider,
            },
          }
        }}
        InputLabelProps={{
          sx: {
            color: theme.palette.text.secondary,
          }
        }}
        sx={{ width: inputWidth }}
      />
    )
  )

  const getDayClassName = (date: Date): string => {
    if (startDate && date.getTime() === startDate.getTime()) {
      return "material-day-selected"
    }
    if (endDate && date.getTime() === endDate.getTime()) {
      return "material-day-selected"
    }
    if (startDate && endDate && date >= startDate && date <= endDate) {
      return "material-day-in-range"
    }
    return ""
  }

  const handleDateRangeChange = (update: [Date | null, Date | null]) => {
    setDateRange(update)

    if (onRangeChange) {
      onRangeChange(update)
    }
  }

  return (
    <Box
      sx={{
        "& .react-datepicker-wrapper": {
          width: width
        }
      }}
    >
      <DatePicker
        selectsRange={true}
        startDate={startDate}
        endDate={endDate}
        onChange={handleDateRangeChange}
        customInput={<CustomInput width={width} />}
        locale={locale}
        monthsShown={2}
        showPopperArrow={false}
        calendarClassName={`material-datepicker ${theme.palette.mode}-theme`}
        dayClassName={getDayClassName}
        popperClassName={`material-datepicker-popper ${theme.palette.mode}-theme`}
        popperPlacement="bottom-start"
        wrapperClassName="date-picker-wrapper"
        isClearable={true}
        calendarContainer={({ className, children }) => (
          <div style={theme.palette.mode === 'dark' ? darkStyles : {}} className={className}>
            {children}
          </div>
        )}
      />
    </Box>
  )
}

export default DateRangePicker
